import { createSlice } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { createCustomAsyncThunk } from "./utils";
import { fileService } from "../services/lessonService";
import React from "react";

export const saveTaskFiles = createCustomAsyncThunk(
    "files/task/post",
    async files => await Promise.all(
        files.map(file => fileService.post(file.file))
    ).then(res => ({ data: res.map((el, index) => ({ ...el, task: files[index].task })) }))
)

export const saveLessonMaterialFiles = createCustomAsyncThunk(
    "files/lessonMaterial/post",
    async files => await Promise.all(files.map(file => fileService.post(file))).then(data => ({ data }))
)

const filesSlice = createSlice({
    name: "files",
    initialState: {
        acceptedFiles: null,
        savedFiles: null,
        savedFilesIsLoading: false,

        error: null,
    },
    reducers: {
        setAcceptedFiles: (state, action) => { state.acceptedFiles = action.payload },
        setSavedFiles: (state, action) => { state.savedFiles = action.payload },
    },
    extraReducers: builder => {
        builder
            .addCase(saveTaskFiles.pending, state => { state.savedFilesIsLoading = true })
            .addCase(saveTaskFiles.fulfilled, (state, action) => {

                let newSavedFiles = state.savedFiles

                action.payload.forEach(file => {
                    if (!newSavedFiles?.[file.task]) newSavedFiles = { ...newSavedFiles, [file.task]: [] }
                    newSavedFiles[file.task] = [...newSavedFiles[file.task], file]
                })

                state.savedFiles = newSavedFiles
                state.savedFilesIsLoading = false
            })
            .addCase(saveTaskFiles.rejected, (state, action) => {
                state.error = action.error
                state.savedFilesIsLoading = false
            })
        
            .addCase(saveLessonMaterialFiles.pending, state => { state.savedFilesIsLoading = true })
            .addCase(saveLessonMaterialFiles.fulfilled, (state, action) => {
                state.savedFiles = action.payload
                state.savedFilesIsLoading = false
            })
            .addCase(saveLessonMaterialFiles.rejected, (state, action) => {
                state.error = action.error
                state.savedFilesIsLoading = false
            })
    }
})

const { setAcceptedFiles, setSavedFiles } = filesSlice.actions

export default filesSlice.reducer

export const useFilesForLessonMaterial = () => {

    const { acceptedFiles, savedFiles } = useSelector(x => x.files)

    const dispatch = useDispatch()

    const handleRemoveAcceptedFile = index => {
        dispatch(setAcceptedFiles(acceptedFiles.filter((_, i) => i !== index)))
    }

    const handleAddFiles = files => {
        dispatch(setAcceptedFiles(files))
        dispatch(saveLessonMaterialFiles(files))
    }

    const handleResetSavedFiles = () => {
        dispatch(setAcceptedFiles(null))
        dispatch(setSavedFiles(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => () => { handleResetSavedFiles() }, [])

    return {
        acceptedFiles, savedFiles,
        handleAddFiles, handleResetSavedFiles, handleRemoveAcceptedFile,
    }
}

export const useFilesForTask = () => {

    const { acceptedFiles, savedFiles } = useSelector(x => x.files)

    const dispatch = useDispatch()

    const handleRemoveAcceptedFile = ({ index, task }) => {
        const newAcceptedFiles = {
            ...acceptedFiles,
            [task]: acceptedFiles[task].filter((_, i) => i !== index)
        }
        if (newAcceptedFiles[task].length === 0) delete newAcceptedFiles[task]
        dispatch(setAcceptedFiles(newAcceptedFiles))
    }

    const handleAddFiles = files => {

        let newAcceptedFiles = acceptedFiles

        files.forEach(file => {
            if (!newAcceptedFiles?.[file.task]) newAcceptedFiles = { ...newAcceptedFiles, [file.task]: [] }
            newAcceptedFiles[file.task] = [...newAcceptedFiles[file.task], file]
        })

        dispatch(setAcceptedFiles(newAcceptedFiles))
        dispatch(saveTaskFiles(files))
    }

    const handleResetSavedFiles = (task) => {

        if (task) {
            const { [task]: x1, ...newAcceptedFiles } = acceptedFiles
            const { [task]: x2, ...newSavedFiles } = savedFiles
            dispatch(setAcceptedFiles(newAcceptedFiles))
            dispatch(setSavedFiles(newSavedFiles))

        } else {
            dispatch(setAcceptedFiles(null))
            dispatch(setSavedFiles(null))
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => () => { handleResetSavedFiles() }, [])

    return {
        acceptedFiles, savedFiles,
        handleAddFiles, handleResetSavedFiles, handleRemoveAcceptedFile,
    }
}